import React, { useState, useEffect } from "react";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Spinner,
  Badge,
  Alert,
  Collapse
} from "reactstrap";
import { useLocation, useNavigate } from 'react-router-dom';
import { getPembahasan } from '../../../services/tryoutService';
import Breadcrumbs from '../../../components/Common/Breadcrumb';

const PembahasanSoal = () => {
  document.title = "Pembahasan Tryout";

  const location = useLocation();
  const navigate = useNavigate();
  const queryParams = new URLSearchParams(location.search);
  const idTryout = queryParams.get('id_tryout');

  const [pembahasanData, setPembahasanData] = useState(null);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPembahasanOpen, setIsPembahasanOpen] = useState(true); // Default open

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      try {
        if (!idTryout) {
          throw new Error("Parameter 'id_tryout' tidak ditemukan di URL. Silakan kembali ke halaman sebelumnya.");
        }

        const response = await getPembahasan(idTryout);
        if (response?.success && response.data) {
          // Asumsi data dari API sudah memiliki 'status_jawaban' jika ingin mewarnai tombol navigasi
          setPembahasanData(response.data);
        } else {
          throw new Error(response?.message || "Gagal memuat data pembahasan atau data tidak lengkap.");
        }
      } catch (err) {
        console.error("Error fetching pembahasan:", err);
        setError(err.message || "Terjadi kesalahan yang tidak terduga saat memuat pembahasan.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [idTryout]);

  const handleNextQuestion = () => {
    if (pembahasanData?.daftar_soal && currentQuestionIndex < pembahasanData.daftar_soal.length - 1) {
      setCurrentQuestionIndex(prev => prev + 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handlePreviousQuestion = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prev => prev - 1);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  };

  const handleQuestionNavigation = (index) => {
    setCurrentQuestionIndex(index);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  if (isLoading) {
    return (
      <div className="d-flex flex-column justify-content-center align-items-center" style={{ minHeight: '80vh' }}>
        <Spinner color="primary" style={{ width: '3rem', height: '3rem' }} />
        <p className="mt-3 text-primary fw-bold">Memuat pembahasan tryout...</p>
      </div>
    );
  }

  if (error) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center py-5" style={{ minHeight: '80vh' }}>
        <Alert color="danger" className="text-center w-75">
          <h5 className="mb-3">Terjadi Kesalahan!</h5>
          <p>{error}</p>
        </Alert>
      </Container>
    );
  }

  if (!pembahasanData || !pembahasanData.daftar_soal || pembahasanData.daftar_soal.length === 0) {
    return (
      <Container className="d-flex flex-column justify-content-center align-items-center py-5" style={{ minHeight: '80vh' }}>
        <Alert color="info" className="text-center w-75">
          <h5 className="mb-3">Tidak Ada Pembahasan</h5>
          <p>Belum ada pembahasan yang tersedia untuk tryout ini.</p>
          <Button color="primary" className="mt-3" onClick={() => navigate('/paket-pembahasan?kategori_paket=pembahasan')}>
            <i className="ri-arrow-left-line me-2"></i> Kembali ke Paket Pembahasan
          </Button>
        </Alert>
      </Container>
    );
  }

  const currentQuestion = pembahasanData.daftar_soal[currentQuestionIndex];
  const totalQuestions = pembahasanData.daftar_soal.length;

  const options = ['a', 'b', 'c', 'd', 'e'];
  const questionOptions = options.map(optChar => ({
    key: `opsi_${optChar}`,
    char: optChar.toUpperCase(),
    text: currentQuestion[`opsi_${optChar}`],
    image: currentQuestion[`image_opsi_${optChar}`],
    isCorrect: currentQuestion.jawaban_benar?.toLowerCase() === optChar.toLowerCase()
  })).filter(opt => (opt.text && opt.text.trim() !== '') || opt.image);

  const getBadgeColor = (jenisSoal) => {
    switch (jenisSoal?.toLowerCase()) {
      case 'twk': return 'primary';
      case 'tiu': return 'info';
      case 'tkp': return 'success';
      default: return 'secondary';
    }
  };

  const getQuestionButtonColor = (index) => {
    if (index === currentQuestionIndex) {
      return 'primary'; // Active question
    }
    // Assuming 'status_jawaban' is available in your API data for each question
    const soalStatus = pembahasanData.daftar_soal[index].status_jawaban;
    switch (soalStatus) {
      case 'benar':
        return 'success';
      case 'salah':
        return 'danger';
      case 'kosong':
        return 'secondary';
      default:
        return 'light'; // Default if no status or not answered
    }
  };

  return (
    <Container className="py-5 bg-light min-vh-100">
      <Row className="justify-content-center">
        {/* --- Kolom Kiri: Soal dan Pembahasan --- */}
        <Col lg={9}>
          <div id="custom-breadcrumbs-container" className="mb-4">
            <Breadcrumbs title="Pembahasan Tryout" breadcrumbItem="Paket Simulasi" />
          </div>

          <Card className="mb-4 shadow-sm">
            <CardBody className="p-4">
              {/* Header Tryout & Jenis Soal */}
              <div className="d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center mb-4 pb-3 border-bottom">
                <div>
                  <h5 className="fw-bold text-primary mb-1">{pembahasanData.nama_tryout}</h5>
                  <p className="text-muted mb-0">Paket: <span className="fw-medium">{pembahasanData.nama_paket}</span></p>
                </div>
                <Badge color={getBadgeColor(currentQuestion.jenis_soal)} className="fs-6 mt-3 mt-md-0 px-3 py-2">
                  {currentQuestion.jenis_soal}
                </Badge>
              </div>

              {/* Judul Soal */}
              <h6 className="fw-bold mb-3 text-secondary">Soal Nomor {currentQuestionIndex + 1} dari {totalQuestions}</h6>

              {/* Konten Soal */}
              <div className="question-section mb-4">
                <div className="p-3 bg-light rounded mb-3 border">
                  {currentQuestion.image_soal && (
                    <div className="text-center mb-3">
                      <img
                        src={currentQuestion.image_soal}
                        alt="Gambar Soal"
                        className="img-fluid rounded shadow-sm"
                        style={{ maxHeight: '300px', maxWidth: '100%', objectFit: 'contain' }}
                      />
                    </div>
                  )}
                  {/* Soal ditampilkan dengan dangerouslySetInnerHTML untuk merender tag HTML */}
                  <div className="question-text" dangerouslySetInnerHTML={{ __html: currentQuestion.soal }} />
                </div>
              </div>

              {/* Daftar Pilihan Jawaban */}
              <div className="options-list mb-4">
                <h6 className="fw-bold mb-3 text-secondary">Pilihan Jawaban:</h6>
                {questionOptions.map((option) => (
                  <div
                    key={option.key}
                    className={`p-3 mb-2 border rounded ${option.isCorrect ? 'border-success bg-light text-success shadow-sm' : 'border-light bg-white'}`}
                  >
                    <div className="d-flex align-items-start">
                      <div className={`option-letter flex-shrink-0 d-flex justify-content-center align-items-center rounded-circle fw-bold me-3 ${option.isCorrect ? 'bg-success text-white' : 'bg-secondary text-white'}`}
                        style={{ width: '30px', height: '30px', fontSize: '0.9rem' }}>
                        {option.char}
                      </div>
                      <div className="option-content flex-grow-1">
                        {/* Opsi jawaban ditampilkan dengan dangerouslySetInnerHTML untuk merender tag HTML */}
                        <div dangerouslySetInnerHTML={{ __html: option.text }} />
                        {option.image && (
                          <div className="mt-2 text-center">
                            <img
                              src={option.image}
                              alt={`Opsi ${option.char}`}
                              className="img-fluid rounded shadow-sm"
                              style={{ maxHeight: '120px', maxWidth: '100%', objectFit: 'contain' }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                    {option.isCorrect && (
                      <div className="text-end mt-2 pt-2 border-top border-success">
                        <Badge color="success" className="p-2">
                          <i className="ri-check-line me-1"></i> Jawaban Benar
                        </Badge>
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Bagian Pembahasan */}
              <div className="discussion-section mb-4">
                <Button
                  color="info"
                  onClick={() => setIsPembahasanOpen(!isPembahasanOpen)}
                  className="w-100 mb-3 shadow-sm"
                  outline={isPembahasanOpen}
                >
                  <i className={`me-2 ${isPembahasanOpen ? 'ri-eye-off-line' : 'ri-eye-line'}`}></i>
                  {isPembahasanOpen ? 'Sembunyikan Pembahasan' : 'Tampilkan Pembahasan'}
                </Button>
                <Collapse isOpen={isPembahasanOpen}>
                  <Card className="border-0 bg-light shadow-sm">
                    <CardBody className="p-3">
                      <h6 className="fw-bold text-info mb-3 d-flex align-items-center">
                        <i className="ri-lightbulb-line me-2"></i> Pembahasan:
                      </h6>
                      <div className="p-3 bg-white rounded border">
                        {currentQuestion.pembahasan ? (
                          // Pembahasan ditampilkan dengan dangerouslySetInnerHTML untuk merender tag HTML
                          <div dangerouslySetInnerHTML={{ __html: currentQuestion.pembahasan }} />
                        ) : (
                          <p className="text-muted text-center py-3">Belum ada pembahasan untuk soal ini.</p>
                        )}
                      </div>
                    </CardBody>
                  </Card>
                </Collapse>
              </div>

              {/* Navigasi Soal Bawah */}
              <div className="d-flex justify-content-between flex-wrap pt-3 border-top">
                <Button
                  color="secondary"
                  onClick={handlePreviousQuestion}
                  disabled={currentQuestionIndex === 0}
                  className="mb-2 mb-md-0 shadow-sm"
                >
                  <i className="ri-arrow-left-line me-2"></i> Sebelumnya
                </Button>
                {currentQuestionIndex < totalQuestions - 1 ? (
                  <Button
                    color="primary"
                    onClick={handleNextQuestion}
                    className="shadow-sm"
                  >
                    Selanjutnya <i className="ri-arrow-right-line ms-2"></i>
                  </Button>
                ) : (
                  <Button
                    color="success"
                    onClick={() => navigate('/paket-simulasi')}
                    className="shadow-sm"
                  >
                    Selesai <i className="ri-check-double-line ms-2"></i>
                  </Button>
                )}
              </div>
            </CardBody>
          </Card>
        </Col>

        {/* --- Kolom Kanan: Navigasi Soal & Informasi Tryout --- */}
        <Col lg={3}>
          <div className="sticky-top" style={{ top: '90px' }}>
            {/* --- Bagian Navigasi Soal --- */}
            <Card className="mb-3 shadow-sm">
              <CardBody className="p-4">
                <h6 className="fw-bold text-center mb-3 text-primary">Navigasi Soal</h6>
                <div className="d-flex flex-wrap justify-content-start" style={{ gap: '8px' }}>
                  {pembahasanData.daftar_soal.map((soal, index) => (
                    <Button
                      key={index}
                      color={getQuestionButtonColor(index)}
                      className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white shadow-sm"
                      style={{
                        width: 'calc((100% / 5) - 6.4px)',
                        height: '40px',
                        fontSize: '0.9rem',
                        flexShrink: 0,
                        flexGrow: 0,
                      }}
                      onClick={() => handleQuestionNavigation(index)}
                    >
                      {index + 1}
                    </Button>
                  ))}
                </div>
                <div className="mt-4">
                  <h6 className="fw-bold text-primary mb-2">Legenda:</h6>
                  <div className="d-flex flex-wrap gap-2">
                    <Badge color="primary" className="p-2">Aktif</Badge>
                    <Badge color="success" className="p-2">Benar</Badge>
                    <Badge color="danger" className="p-2">Salah</Badge>
                    <Badge color="secondary" className="p-2">Belum Dijawab</Badge>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* --- Bagian Informasi Tryout --- */}
            <Card className="shadow-sm">
              <CardBody className="p-4">
                <h6 className="fw-bold text-center mb-3 text-primary">Informasi Tryout</h6>
                <ul className="list-unstyled mb-0">
                  <li className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <span className="text-muted fw-medium">Jumlah Soal:</span>
                    <span className="fw-bold">{pembahasanData.jumlah_soal}</span>
                  </li>
                  <li className="d-flex justify-content-between align-items-center mb-2 pb-2 border-bottom">
                    <span className="text-muted fw-medium">Durasi:</span>
                    <span className="fw-bold">{pembahasanData.durasi_menit} menit</span>
                  </li>
                  <li className="d-flex justify-content-between align-items-center">
                    <span className="text-muted fw-medium">Jenis Paket:</span>
                    <span className="fw-bold">{pembahasanData.nama_paket}</span>
                  </li>
                </ul>
              </CardBody>
            </Card>
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export default PembahasanSoal;